import { useEffect, useState } from "react";
import { createLocalTracks } from "livekit-client";

export function useDeviceCheck({
    initialMicEnabled = true,
    initialCameraEnabled = true,
    canPublishAudio = true,
    canPublishVideo = true,
}) {
    const [deviceStatus, setDeviceStatus] = useState({
        audioAvailable: false,
        videoAvailable: false,
        audioAllowed: false,
        videoAllowed: false,
        checking: true,
        warningDismissed: false,
    });

    useEffect(() => {
        let cancelled = false;

        const checkDevices = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();

                const hasAudioInput = devices.some((d) => d.kind === "audioinput");
                const hasVideoInput = devices.some((d) => d.kind === "videoinput");

                const shouldRequestAudio =
                    hasAudioInput && initialMicEnabled && canPublishAudio;
                const shouldRequestVideo =
                    hasVideoInput && initialCameraEnabled && canPublishVideo;

                if (!shouldRequestAudio && !shouldRequestVideo) {
                    if (cancelled) return;

                    setDeviceStatus({
                        audioAvailable: hasAudioInput,
                        videoAvailable: hasVideoInput,
                        audioAllowed: false,
                        videoAllowed: false,
                        checking: false,
                    });
                    return;
                }

                let audioAllowed = false;
                let videoAllowed = false;

                try {
                    const tracks = await createLocalTracks({
                        audio: shouldRequestAudio,
                        video: shouldRequestVideo,
                    });

                    if (cancelled) {
                        tracks.forEach((t) => t.stop());
                        return;
                    }

                    audioAllowed = tracks.some((t) => t.kind === "audio");
                    videoAllowed = tracks.some((t) => t.kind === "video");

                    tracks.forEach((t) => t.stop());
                } catch (permissionError) {
                    // Silent fallback - banner will show instead (same behavior)
                    console.warn(
                        "Permission denied or device unavailable:",
                        permissionError?.name,
                    );
                    audioAllowed = false;
                    videoAllowed = false;
                }

                if (cancelled) return;

                setDeviceStatus({
                    audioAvailable: hasAudioInput,
                    videoAvailable: hasVideoInput,
                    audioAllowed,
                    videoAllowed,
                    checking: false,
                });
            } catch (err) {
                console.error("Device enumeration failed:", err);

                if (cancelled) return;

                setDeviceStatus({
                    audioAvailable: false,
                    videoAvailable: false,
                    audioAllowed: false,
                    videoAllowed: false,
                    checking: false,
                });
            }
        };

        checkDevices();

        return () => {
            cancelled = true;
        };
    }, [initialMicEnabled, initialCameraEnabled, canPublishAudio, canPublishVideo]);

    return { deviceStatus, setDeviceStatus };
}